import { useMemo } from 'react';
import {
  TApiApplicationDocument,
} from '~/modules/covenants/types.ts';
import {
  buildApplicationDocumentRowKey,
  buildDocumentsForIITSigning,
} from '~/modules/covenants/utils.ts';
import {
  DIIA_DOCUMENTS_LIMIT_MESSAGE,
  MAX_DIIA_DOCUMENTS_FOR_SIGNING,
} from '~/modules/covenants/CovenantsDataApplicationForm/constants.ts';
import type { IUseCovenantsSigningStateParams } from '~/modules/covenants/CovenantsDataApplicationForm/types.ts';
import { covenantsBackdropSx } from '~/modules/covenants/constants.tsx';
import {
  hasAnyApplicationFiles,
  hasExternalSignatureForDocument,
} from '~/modules/covenants/CovenantsDataApplicationForm/utils.ts';

export const useCovenantsSigningState = ({
  documentRows,
  signatureFlowDocuments,
  localFiles,
  signatureValidationState,
  iitHashes,
  applicationUnsignedDocumentsCount,
  isDiiaSigningStarted,
  isSignatureFlowLoading,
  isIITLoading,
  isDocumentUploadLoading,
}: IUseCovenantsSigningStateParams) => {
  const documentsForSignatureFlow = useMemo(
    () => (signatureFlowDocuments.length ? signatureFlowDocuments : documentRows),
    [signatureFlowDocuments, documentRows]
  );

  const signableDocuments = useMemo(
    () =>
      documentsForSignatureFlow.filter((document) => {
        const localFile = localFiles[buildApplicationDocumentRowKey(document)];
        const hasDocumentFile = !!document.fileName || !!localFile?.file;

        return (
          hasDocumentFile &&
          !document.isCustomSignFile &&
          !hasExternalSignatureForDocument(document, localFiles, signatureValidationState) &&
          document.status !== 'ACCEPTED' &&
          document.status !== 'SENT'
        );
      }),
    [documentsForSignatureFlow, localFiles, signatureValidationState]
  );

  const hasAnyFiles = useMemo(
    () => hasAnyApplicationFiles(documentsForSignatureFlow, localFiles),
    [documentsForSignatureFlow, localFiles]
  );

  const needsSignByLoadedRows = signableDocuments.length > 0;
  const needsSign =
    typeof applicationUnsignedDocumentsCount === 'number'
      ? applicationUnsignedDocumentsCount > 0
      : needsSignByLoadedRows;
  const diiaSignableDocuments = signableDocuments.filter(
    (document): document is TApiApplicationDocument & { id: string } =>
      typeof document.id === 'string'
  );
  const isDiiaDocumentsLimitExceeded =
    typeof applicationUnsignedDocumentsCount === 'number' &&
    applicationUnsignedDocumentsCount > MAX_DIIA_DOCUMENTS_FOR_SIGNING;
  const diiaDisabledMessage = isDiiaDocumentsLimitExceeded
    ? DIIA_DOCUMENTS_LIMIT_MESSAGE
    : undefined;
  const documentsForSigning = buildDocumentsForIITSigning(signableDocuments, iitHashes, localFiles);
  const isSignatureInteractionBlocked =
    isDiiaSigningStarted || isSignatureFlowLoading || isIITLoading;
  const overlayLoaderSx = isDocumentUploadLoading
    ? (theme: Parameters<typeof covenantsBackdropSx>[0]) =>
        covenantsBackdropSx(theme, { isBlocking: true })
    : covenantsBackdropSx;

  return {
    hasAnyFiles,
    needsSign,
    needsSignByLoadedRows,
    diiaSignableDocuments,
    isDiiaDocumentsLimitExceeded,
    diiaDisabledMessage,
    documentsForSigning,
    isSignatureInteractionBlocked,
    overlayLoaderSx,
  };
};
